import ReactEcharts from 'echarts-for-react';
import { useEffect, useRef, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { ChartControlsProps } from '../../redux/ChartPoperties/ChartControlsInterface';
import { formatChartLabelValueForSelectedMeasure } from '../ChartOptions/Format/NumberFormatter';
import {
  ChartsMapStateToProps,
  ChartsReduxStateProps,
  FormatterValueProps,
} from './ChartsCommonInterfaces';
import {
  displayName,
  checkUidPresenceInCheckedMeasureForTooltip,
  shouldChartDisplayWithSecurity,
  limitChartData,
} from '../CommonFunctions/CommonFunctions';

import { palette } from '../../';

import EmptyQuery from './EmptyQuery';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux';
import formatDate from '../ChartOptions/Format/dateFormatterFunction';
import { updateHoveredItems } from '../../redux/TabTile/TabActions';
import { tooltipStyle } from '../Common/EChartTooltip';
import useCombinedColorSchemes from '../../hooks/useCombinedColorSchemes';
import { messages } from '../CommonFunctions/aliases';
import { ColorSchemes } from '../ChartOptions/Color/ColorScheme';
import { toast } from 'react-toastify';
import {
  LABEL_THRESHOLDS,
  LEGEND_THRESHOLDS,
  MARGIN_THRESHOLDS,
} from '../ChartOptions/ChartThresholds';

const RoseChart = ({
  //props
  propKey,
  graphDimension,
  chartArea,
  graphTileSize,
  colorScheme,
  fontFamily,
  softUI,

  //state
  chartProperties,
  chartControls,
}: ChartsReduxStateProps) => {
  var chartControl: ChartControlsProps = chartControls.properties[propKey];
  let chartData: any[] = chartControl.chartData ? chartControl.chartData : [];
  const currentControls = chartControls.properties[propKey].roseChartControls || {};
  const { manualColor, nodeValuesColors, allSeriesColor } = currentControls;

  const {
    selectedMeasureName,
    tabState,
    selectedTab,
    showDash,
    tooltipFormats,
    chartAxes,
    isTooltipEnabled,
    chartType,
  } = useSelector((state: RootState) => ({
    selectedMeasureName:
      state.chartControls.properties[propKey]?.formatOptions?.dateFormats?.selectedMeasureName,
    tabState: state.tabState,
    selectedTab: state.tabTileProps.selectedTabId,
    showDash: state.tabTileProps.showDash,
    tooltipFormats: state.chartControls.properties[propKey]?.formatOptions?.tooltipFormats,
    chartAxes: state.chartProperties.properties[propKey]?.chartAxes,
    isTooltipEnabled:
      state.chartControls?.properties[propKey]?.formatOptions?.tooltipFormats?.isEnable,
    chartType: state.chartProperties?.properties[propKey]?.chartType,
  }));

  const [chartDataKeys, setChartDataKeys] = useState<any>([]);

  const chartRef = useRef<any>(null);
  const chartInstanceRef = useRef<echarts.ECharts | null>(null);
  const dispatch = useDispatch();

  const mouseOverInteractivity =
    tabState.tabs[selectedTab]?.interactivity?.mouseOverInteractivity || false;

  const modifiedChartData = limitChartData(chartData, 50, chartAxes[2]?.fields[0]?.displayname);

  let objKey = chartProperties.properties[propKey]?.chartAxes[1]?.fields[0]?.displayname;

  useEffect(() => {
    if (chartData.length >= 1) {
      if (typeof chartData === 'object' && chartData.length > 0) {
        setChartDataKeys(Object.keys(chartData[0]));
      }
      // if ("timeGrain" in chartProperties.properties[propKey].chartAxes[1].fields[0]) {
      // 	   objKey = `${chartProperties.properties[propKey].chartAxes[1].fields[0].timeGrain} of ${chartProperties.properties[propKey].chartAxes[1].fields[0].fieldname}`;
      // } else {
      // 	   objKey = chartProperties.properties[propKey].chartAxes[1].fields[0].fieldname;
      // }
      if (objKey) {
        // Map over chart data and replace nulls with "(Blank)" without modifying the original array
        const mappedChartData = chartControl.chartData.map((el: any) => {
          const newEl = { ...el }; // Create a shallow copy of the element to avoid direct mutation

          //Converts the negative value from integer to string
          if (objKey in newEl && typeof newEl[objKey] === 'number' && !isNaN(newEl[objKey])) {
            newEl[objKey] = newEl[objKey].toString();
          }
          if (newEl[objKey] === null || newEl[objKey] === 'null') {
            newEl[objKey] = '(Blank)';
          } else if (typeof newEl[objKey] === 'boolean') {
            newEl[objKey] = newEl[objKey] ? 'True' : 'False';
          }
          return newEl;
        });

        if (JSON.stringify(chartControl.chartData) !== JSON.stringify(mappedChartData)) {
          chartControl.chartData = mappedChartData;
        }
      }
    }
  }, [chartData, chartControl]);

  useEffect(() => {
    if (chartData.length > 50 && !showDash) {
      toast.warning(`${messages.chart.excessiveData} 50 records`, {
        position: 'top-center',
        autoClose: 10000,
        toastId: 'chart-data-limit-warning',
        style: {
          top: '10px',
        },
      });
    }
  }, [chartData]);

  const allThemes = useCombinedColorSchemes();
  const chartThemes = allThemes.filter((el) => {
    if (!chartControl.colorScheme || chartArea === 'dashboard') {
      return el.name === colorScheme;
    } else {
      return el.name === chartControl.colorScheme;
    }
  });

  const [hoveredColumns, setHoveredColumns] = useState<Record<string, any>[]>([]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (
        !mouseOverInteractivity ||
        (tabState.tabs[selectedTab].hoveredPropkey &&
          tabState.tabs[selectedTab].hoveredPropkey === propKey)
      ) {
        return;
      }
      const chartInstance = chartInstanceRef.current;
      if (!chartInstance || chartInstance.isDisposed?.()) return;

      const index = modifiedChartData.findIndex((item) =>
        tabState.tabs[selectedTab].hoveredItems?.some(
          (hovered: Record<string, any>) =>
            String(hovered[chartDataKeys[0]]) === String(item[chartDataKeys[0]]),
        ),
      );

      if (index >= 0) {
        chartInstance.dispatchAction({
          type: 'highlight',
          seriesIndex: 0,
          dataIndex: index,
        });
      } else {
        chartInstance.dispatchAction({
          type: 'downplay',
          seriesIndex: 0,
        });
      }
    }, 30); // slight delay helps when charts update

    return () => clearTimeout(timeout);
  }, [tabState.tabs[selectedTab].hoveredItems, modifiedChartData]);

  const hoveredRef = useRef<Record<string, any> | null>(null);

  const hoverdTimeout = useRef<NodeJS.Timeout | null>(null);

  const handleChartReady = (chartInstance: any) => {
    if (!mouseOverInteractivity) return;

    chartInstanceRef.current = chartInstance;

    chartInstance.on('mouseover', (params: any) => {
      if (
        params?.seriesName &&
        params?.name &&
        hoveredRef.current?.[chartDataKeys[0]] !== params.name
      ) {
        if (hoverdTimeout.current) clearTimeout(hoverdTimeout.current);

        hoverdTimeout.current = setTimeout(() => {
          const newEntry = { [chartDataKeys[0]]: params.name };
          hoveredRef.current = newEntry;

          setHoveredColumns((prev) => {
            const isDuplicate = prev.some((item) => item[chartDataKeys[0]] === params.name);
            if (!isDuplicate) {
              return [...prev, newEntry];
            }
            return prev;
          });
        }, 200);
      }
    });

    chartInstance.on('mouseout', (params: any) => {
      if (hoverdTimeout.current) clearTimeout(hoverdTimeout.current);
      if (params?.name) {
        hoveredRef.current = null;
        setHoveredColumns([]);
      }
    });
  };

  useEffect(() => {
    dispatch(updateHoveredItems(selectedTab, hoveredColumns, propKey));
  }, [hoveredColumns]);

  const chartInstance = chartRef.current?.getEchartsInstance?.();
  const currentOption = chartInstance?.getOption();
  const [currentLegendSelected, setCurrentLegendSelected] = useState<any>(
    currentOption?.legend?.[0]?.selected ?? {},
  );

  const RenderChart = () => {
    const themeColors = chartThemes[0]?.colors || ColorSchemes[0].colors;
    const finalChartData = modifiedChartData.map((item, index) => {
      const dimensionKey = chartDataKeys[0];
      const measureKey = chartDataKeys[1];
      const dimensionValue = String(item[dimensionKey]);
      const measureValue = item[measureKey];
      let sliceColor = themeColors[index % themeColors.length];

      if (manualColor) {
        const lookupKey = dimensionValue === '(Blank)' ? 'null' : dimensionValue;
        if (nodeValuesColors && nodeValuesColors[lookupKey]) {
          sliceColor = nodeValuesColors[lookupKey];
        } else if (allSeriesColor) {
          sliceColor = allSeriesColor;
        }
      }

      return {
        name: dimensionValue,
        value: measureValue,
        ...item,
        itemStyle: {
          color: sliceColor,
          borderRadius: 5,

          ...(softUI
            ? {
                shadowColor: 'rgba(0, 0, 0, 0.5)',
                shadowBlur: 10,
                shadowOffsetX: 3,
                shadowOffsetY: 3,
              }
            : {}),
        },
      };
    });

    const onLegendSelectChanged = (params: any) => {
      setCurrentLegendSelected(params.selected);
    };

    return (
      <>
        <ReactEcharts
          ref={chartRef}
          onChartReady={handleChartReady}
          onEvents={{
            legendselectchanged: onLegendSelectChanged,
          }}
          // theme={chartControl.colorScheme}
          style={{
            // padding: "1rem",
            width: graphDimension.width,
            height: graphDimension.height,
            overflow: 'hidden',
            border: chartArea ? 'none' : graphTileSize ? 'none' : '1px solid rgb(238,238,238)',
          }}
          option={{
            color: chartThemes[0]?.colors || [],
            backgroundColor: chartThemes[0]?.background || [],
            // animation: chartArea ? false : true, // Turning animation off for current release, to be added later on
            animation: false,
            legend: {
              // textStyle :{color : getContrastColor(chartThemes[0].background)},
              type: 'scroll',
              show:
                Math.floor(graphDimension.height) > LEGEND_THRESHOLDS.rose.h &&
                Math.floor(graphDimension.width) > LEGEND_THRESHOLDS.rose.w
                  ? chartControl.legendOptions?.showLegend
                  : false,
              itemHeight: chartControl.legendOptions?.symbolHeight,
              itemWidth: chartControl.legendOptions?.symbolWidth,
              itemGap: chartControl.legendOptions?.itemGap,

              left: chartControl.legendOptions?.position?.left,
              top: chartControl.legendOptions?.position?.top,
              orient: chartControl.legendOptions?.orientation,
              selected: currentLegendSelected,
              textStyle: {
                color: chartThemes[0]?.dark ? '#ffffff' : palette.primary.contrastText,
                fontFamily: fontFamily,
              },
              formatter: function (name: string) {
                const measureField = chartAxes
                  ?.flatMap((axis: any) => axis?.fields || [])
                  ?.find((f: any) => f.displayname === objKey);

                const measureUid = measureField?.uId;
                const labelFormat = chartControls.properties[propKey]?.formatOptions?.labelFormats
                  ?.measureFormats?.[measureUid] as any;

                if (labelFormat?.finalDateFormat) {
                  return formatDate(name, labelFormat);
                }

                return name;
              },
            },

            tooltip: {
              show: isTooltipEnabled,
              useHTML: true,
              trigger: 'item',
              backgroundColor: 'white',
              borderWidth: 1,
              padding: 0,
              textStyle: {
                color: '#333',
                fontSize: 16,
              },
              appendToBody: true,
              extraCssText:
                'box-shadow: 0 2px 8px rgba(0,0,0,0.15);white-space: normal; word-break: break-word;',
              formatter: function (params: any) {
                const tempData = params.data;
                const dimensionKey = objKey;
                const measureKey = chartAxes.find((axis: any) => axis.name === 'Measure').fields[0]
                  .displayname;
                const dimensionValue = tempData[dimensionKey as string];
                //----------remove unchecked measure or any other field value of type date inside tooltip----------//
                const _data: { [key: string]: string | number } = {};
                Object.keys(tempData).forEach((key: string) => {
                  if (key === 'name' || key === 'value' || key === 'itemStyle') return;
                  const resp = checkUidPresenceInCheckedMeasureForTooltip(
                    chartAxes,
                    key,
                    tooltipFormats?.checkedMeasure,
                  );
                  if (resp) {
                    _data[key] = tempData[key];
                  }
                });
                let mergedData = { ..._data };
                //-- send extra responses in _data so that it maps and shows all key value pairs including extra measure (if any)--//
                //----get extra response corresponding to a particular dimension----//
                const extraResponses = tooltipFormats.extraMeasureFieldsResponses;
                if (tooltipFormats && extraResponses.length > 0) {
                  const extraFieldObj = extraResponses.find(
                    (obj: any) =>
                      String(obj[dimensionKey as string]) === String(dimensionValue as string),
                  );
                  if (extraFieldObj) {
                    //---- Remove all dimension keys from extraFieldObj ----//
                    const { [dimensionKey as string]: _, ...restExtraFields } = extraFieldObj;
                    //-----Remove unchecked measure field after adding extra measure ----//
                    let filteredFields = { ...mergedData, ...restExtraFields };
                    const isPresent = checkUidPresenceInCheckedMeasureForTooltip(
                      chartAxes,
                      measureKey,
                      tooltipFormats?.checkedMeasure,
                    );
                    if (!isPresent) {
                      delete filteredFields[measureKey];
                    }
                    //---------send final response in tooltip data i.e checked measure plus extra fields-------//
                    mergedData = { ...filteredFields };
                  }
                }
                return tooltipStyle(
                  params.color,
                  mergedData,
                  tooltipFormats,
                  chartAxes,
                  dimensionKey,
                  undefined,
                  undefined,
                  fontFamily,
                );
              },
            },

            series: [
              {
                type: 'pie',
                roseType: 'area',
                startAngle: chartControl.axisOptions.pieAxisOptions.pieStartAngle,
                clockwise: chartControl.axisOptions.pieAxisOptions.clockWise,
                data: finalChartData,

                emphasis: {
                  focus: 'self',
                },
                blur: {
                  itemStyle: {
                    opacity: 0.5,
                  },
                },
                label: {
                  position: chartControl.labelOptions.pieLabel.labelPosition,
                  show:
                    Math.floor(graphDimension.height) > LABEL_THRESHOLDS.pieArea.h &&
                    Math.floor(graphDimension.width) > LABEL_THRESHOLDS.pieArea.w
                      ? chartControl.labelOptions.showLabel
                      : false,
                  fontSize: chartControl.labelOptions.fontSize,
                  fontFamily: fontFamily,
                  color: chartControl.labelOptions.labelColor
                    ? chartControl.labelOptions.labelColor
                    : chartThemes[0]?.dark
                      ? '#ffffff'
                      : '#757474',
                  padding: chartControl.labelOptions.labelBackground
                    ? [4, 8]
                    : [
                        chartControl.labelOptions.pieLabel.labelPadding,
                        chartControl.labelOptions.pieLabel.labelPadding,
                        chartControl.labelOptions.pieLabel.labelPadding,
                        chartControl.labelOptions.pieLabel.labelPadding,
                      ],
                  backgroundColor: chartControl.labelOptions.labelBackground
                    ? chartControl.labelOptions.labelBackgroundColor
                    : 'transparent',
                  borderRadius: chartControl.labelOptions.labelBackground ? 4 : 0,

                  formatter: (value: FormatterValueProps) => {
                    const columnName = displayName(
                      chartProperties.properties[propKey].chartAxes[2].fields[0],
                    );
                    let rawValue = value.data ? value.data[columnName] : undefined;
                    if (rawValue === undefined) rawValue = value.value;

                    if (chartDataKeys && rawValue !== undefined) {
                      const formattedValue = formatChartLabelValueForSelectedMeasure(
                        chartControls.properties[propKey],
                        chartProperties.properties[propKey],
                        rawValue,
                        columnName,
                      );
                      return formattedValue;
                    }
                  },
                },
                radius: [
                  chartControl.chartMargin.innerRadius + '%',
                  Math.floor(graphDimension.height) <= MARGIN_THRESHOLDS.compact.h
                    ? '90%'
                    : chartControl.chartMargin.outerRadius + '%',
                ],
              },
            ],
          }}
        />
      </>
    );
  };

  const emptyQueryCondition =
    shouldChartDisplayWithSecurity(chartAxes, chartType, chartData) === false ||
    chartData.length < 1;

  return emptyQueryCondition ? (
    <EmptyQuery
      graphDimension={graphDimension}
      propKey={propKey}
      chartArea={chartArea}
      graphTileSize={graphTileSize}
      chartThemes={chartThemes}
      showEmptyQueryMessageWithSecurity={
        !shouldChartDisplayWithSecurity(chartAxes, chartType, chartData) ? true : false
      }
    />
  ) : (
    <RenderChart />
  );
};

const mapStateToProps = (state: ChartsMapStateToProps, ownProps: any) => {
  return {
    chartProperties: state.chartProperties,
    chartControls: state.chartControls,
  };
};

export default connect(mapStateToProps, null)(RoseChart);
